import { FC, forwardRef } from 'react';
import { useSortableLocalUpdate } from '../journey/utils/useSortableLocalUpdate';
import { ReactSortable, SortableEvent } from 'react-sortablejs';
import { Card, StackProps, Stack, Typography, IconButton } from '@mui/material';
import { graphql } from '../gql';
import { useApiMutation } from '../query/hooks';
import { OrderMetrics } from './OrderDashboardMetricsDrawer';
import { DND_DELAY } from '../journey/detail/detailView/DetailTable';
import { useSnackbar } from '../store/snackbar';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { useIntl } from 'react-intl';
import { DeleteOutlineOutlined } from '@mui/icons-material';

type Props = Readonly<{
	dashboardId: string;
	dashboardMetrics: OrderMetrics;
}>;

const updateMetricsOrderMutation = graphql(`
	mutation dashboard_updateMetricsOrder($data: [DashboardUpdateDashboardMetricsEntityRelationInput!], $id: UUID!) {
		updateDashboard(by: { id: $id }, data: { dashboardMetrics: $data }) {
			ok
			errorMessage
		}
	}
`);

const deleteDashboardMetricMutation = graphql(`
	mutation DeleteDashboardMetric($contentMetricId: UUID!) {
		deleteDashboardMetric(by: { contentMetric: { id: $contentMetricId } }) {
			ok
			errorMessage
		}
	}
`);

export const OrderDashboardMetrics: FC<Props> = ({ dashboardMetrics, dashboardId }) => {
	const { state: metricsState, setState: setMetricsState, optimisticReorder, updateFailed } = useSortableLocalUpdate(dashboardMetrics);
	const { mutate: updateMetricsOrder } = useApiMutation(updateMetricsOrderMutation);
	const { openSnackbar } = useSnackbar();
	const intl = useIntl();

	const { mutate: deleteDashboardMetric } = useApiMutation(deleteDashboardMetricMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.deleteDashboardMetric.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'metrics-widget.source.dashboard-delete.success' }),
					error: intl.formatMessage({ id: 'metrics-widget.source.dashboard-delete.error' }),
				},
				errorMessage: result.deleteDashboardMetric.errorMessage,
				openSnackbar,
			});
		},
	});

	const handleReorderMetrics = (event: SortableEvent) => {
		const resultStateCopy = optimisticReorder(event);

		if (resultStateCopy?.length != null) {
			setMetricsState(resultStateCopy);

			updateMetricsOrder(
				{
					id: dashboardId,
					data: resultStateCopy.map(({ id }, index) => ({
						update: {
							by: { id },
							data: {
								order: index,
							},
						},
					})),
				},
				{
					onSettled: (result) => {
						result?.updateDashboard.errorMessage && console.error(result.updateDashboard.errorMessage);
						updateFailed(result?.updateDashboard.ok);
					},
				},
			);
		}
	};

	return (
		<ReactSortable
			delayOnTouchOnly
			delay={DND_DELAY}
			tag={ForwardedStack}
			list={metricsState as OrderMetrics}
			setList={() => {}}
			onEnd={(event) => handleReorderMetrics(event)}
		>
			{metricsState.map(({ id, ...rest }) => (
				<Card sx={{ p: 2, cursor: 'grab', ':active': { cursor: 'grabbing' } }} key={id}>
					<Stack
						flexDirection={'row'}
						sx={{ ':hover': { button: { opacity: 1, transition: 'all 0.15s ease-in' } } }}
						justifyContent={'space-between'}
						alignItems={'center'}
						gap={2}
						overflow={'auto'}
					>
						<Typography
							sx={{
								'-webkit-line-clamp': '2',
								display: '-webkit-box',
								'-webkit-box-orient': 'vertical',
								wordBreak: 'break-word',
								overflow: 'hidden',
							}}
						>
							{rest.contentMetric?.metric?.name}
						</Typography>
						<IconButton sx={{ opacity: 0 }} onClick={() => deleteDashboardMetric({ contentMetricId: rest.contentMetric?.id ?? '' })}>
							<DeleteOutlineOutlined color={'error'} />
						</IconButton>
					</Stack>
				</Card>
			))}
		</ReactSortable>
	);
};

const ForwardedStack = forwardRef<HTMLDivElement, StackProps>((props, ref) => (
	<Stack {...props} ref={ref} width={'100%'} gap={2} direction={'column'} alignItems={'stretch'} />
));
